/**
 * Questionnaire Page - עמוד שאלון סיכון
 * Risk assessment questionnaire with full wizard implementation
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Check, HelpCircle } from 'lucide-react'
import QuestionnaireWizard from '../components/questionnaire/QuestionnaireWizard'
import api, { handleApiError } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { QuestionResponse } from '../types'

// Risk profile translations
const RISK_PROFILE_TRANSLATIONS: Record<string, string> = {
  'conservative': 'סיכון נמוך',
  'moderate_conservative': 'סיכון נמוך-בינוני',
  'moderate': 'סיכון בינוני',
  'moderate_aggressive': 'סיכון בינוני-גבוה',
  'aggressive': 'סיכון גבוה',
  'moderately_conservative': 'סיכון נמוך-בינוני',
  'moderately_aggressive': 'סיכון בינוני-גבוה',
  'very_aggressive': 'סיכון גבוה'
}

// Onboarding stepper steps
const ONBOARDING_STEPS = [
  { label: 'פרטים', key: 'details' },
  { label: 'הסכם', key: 'agreement' },
  { label: 'שאלון', key: 'questionnaire' },
  { label: 'אישור סיכון', key: 'risk-approval' },
]
const CURRENT_STEP_KEY = 'questionnaire'

function OnboardingStepper() {
  const currentIndex = ONBOARDING_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY)

  return (
    <div className="flex items-center justify-center gap-0 mb-3" dir="ltr">
      {ONBOARDING_STEPS.map((step, index) => {
        const isCompleted = index < currentIndex
        const isCurrent = index === currentIndex
        return (
          <div key={step.key} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                  isCompleted
                    ? 'bg-green-500 border-green-500 text-white'
                    : isCurrent
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {isCompleted ? <Check size={12} strokeWidth={3} /> : index + 1}
              </div>
              <span className={`mt-0.5 text-[10px] font-medium whitespace-nowrap ${
                isCompleted ? 'text-green-600' : isCurrent ? 'text-blue-700' : 'text-gray-400'
              }`}>
                {step.label}
              </span>
            </div>
            {index < ONBOARDING_STEPS.length - 1 && (
              <div className={`h-0.5 w-10 mx-1 mb-3.5 transition-all ${
                index < currentIndex ? 'bg-green-400' : 'bg-gray-200'
              }`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function QuestionnairePage() {
  const navigate = useNavigate()
  const { updateUser } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [questionnaireId, setQuestionnaireId] = useState<string | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const [existingQuestionnaire, setExistingQuestionnaire] = useState<any>(null)
  const [showResults, setShowResults] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [pendingResult, setPendingResult] = useState<{ responses: QuestionResponse[]; riskScore: number; riskProfile: string } | null>(null)

  // Lock body scroll for the duration of the questionnaire
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // Check for existing questionnaire and initialize if needed
  useEffect(() => {
    const initQuestionnaire = async () => {
      try {
        setIsInitializing(true)

        // First, check if user has a valid completed questionnaire
        try {
          const currentResponse = await api.get('/questionnaire/current')
          if (currentResponse.data?.questionnaire) {
            // User has a valid questionnaire - show results only
            const q = currentResponse.data.questionnaire
            setExistingQuestionnaire(q)
            setShowResults(true)
            setIsInitializing(false)
            // Sync store so ProtectedRoute doesn't redirect back here
            updateUser({
              lastQuestionnaireCompletedAt: q.completedAt || q.updatedAt,
              questionnaireExpiresAt: q.expiresAt,
              currentRiskScore: q.normalizedScore,
            })
            return
          }
        } catch (err: any) {
          // No valid questionnaire found (404) or expired - allow new one
          if (err.response?.status !== 404) {
            console.error('Error checking current questionnaire:', err)
          }
        }

        // No valid questionnaire - start a new one
        const response = await api.post('/questionnaire/start', {
          version: '1.0'
        })
        setQuestionnaireId(response.data.questionnaire._id)
      } catch (err) {
        setError(handleApiError(err))
      } finally {
        setIsInitializing(false)
      }
    }

    initQuestionnaire()
  }, [])

  const submitToBackend = async (result: { responses: QuestionResponse[]; riskScore: number; riskProfile: string }) => {
    if (!questionnaireId) {
      setSaveError('שגיאה: מזהה שאלון חסר. נסה לרענן את הדף.')
      return
    }

    setIsSubmitting(true)
    setSaveError(null)

    try {
      const answers = result.responses.map((r) => ({
        questionId: `Q${r.questionId}`,
        optionId: `Q${r.questionId}A${r.answer}`,
      }))

      const response = await api.post('/questionnaire/submit', {
        questionnaireId,
        answers,
        disclosuresAcknowledged: true,
      })

      if (response.data?.result) {
        updateUser({
          currentRiskScore: response.data.result.score,
          lastQuestionnaireCompletedAt: new Date().toISOString(),
          questionnaireExpiresAt: response.data.validUntil,
          riskApprovalSigned: false,
        })
      }

      setPendingResult(null)
      navigate('/risk-approval', { replace: true })
    } catch (err) {
      setSaveError(handleApiError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleQuestionnaireComplete = async (result: {
    responses: QuestionResponse[]
    riskScore: number
    riskProfile: string
  }) => {
    setPendingResult(result)
    await submitToBackend(result)
  }

  const handleRetry = () => {
    if (pendingResult) submitToBackend(pendingResult)
  }

  const handleStartOver = () => {
    localStorage.removeItem('questionnaire-progress')
    window.location.reload()
  }

  // Results card (used for both new completions and existing questionnaire)
  const renderResults = (score: number, profile: string, expiresAt?: string) => (
    <div className="flex-1 overflow-y-auto">
      <div className="flex flex-col gap-4 py-2">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-2">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">שאלון הסיכון הושלם!</h2>
          <p className="text-sm text-gray-500 mt-1">הפרופיל שלך נשמר במערכת</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 text-center">
          <p className="text-xs text-gray-500 mb-1">פרופיל הסיכון שלך</p>
          <p className="text-4xl font-bold text-indigo-600 mb-1">{score}/5</p>
          <p className="text-lg font-semibold text-gray-800">{profile}</p>
          {expiresAt && (
            <p className="text-xs text-gray-400 mt-2">תוקף עד {new Date(expiresAt).toLocaleDateString('he-IL')}</p>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 text-center">
          פרופיל הסיכון תקף ל-12 חודשים. ניתן לעדכן לאחר תום התקופה.
        </div>

        <div className="flex flex-col gap-2">
          <button onClick={() => navigate('/dashboard', { replace: true })} className="btn btn-primary w-full">
            המשך לדשבורד
          </button>
          <button onClick={() => navigate('/chat', { replace: true })} className="btn btn-outline w-full">
            התחל צ'אט ייעוץ
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-gray-50 flex flex-col overflow-hidden z-20" dir="rtl">
      <div className="flex-1 overflow-hidden flex flex-col p-4 max-w-lg mx-auto w-full">

        {/* Initializing */}
        {isInitializing ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="spinner w-10 h-10" />
              <p className="text-gray-600 text-sm">מאתחל שאלון...</p>
            </div>
          </div>

        ) : saveError ? (
          /* Save failed — show error with retry / start over */
          <div className="flex-1 flex flex-col justify-center gap-4">
            <div className="bg-white rounded-2xl border border-red-200 p-5 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-3">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">שגיאה בשמירת השאלון</h2>
              <p className="text-sm text-gray-600 mb-4">{saveError}</p>
              <div className="flex flex-col gap-2">
                <button onClick={handleRetry} className="btn btn-primary w-full">
                  נסה שוב
                </button>
                <button onClick={handleStartOver} className="btn btn-outline w-full">
                  התחל מחדש
                </button>
              </div>
            </div>
          </div>

        ) : showResults && existingQuestionnaire ? (
          /* Already has a questionnaire */
          renderResults(
            existingQuestionnaire.normalizedScore,
            typeof existingQuestionnaire.riskProfile === 'string'
              ? RISK_PROFILE_TRANSLATIONS[existingQuestionnaire.riskProfile] || existingQuestionnaire.riskProfile
              : existingQuestionnaire.riskProfile?.levelHebrew || existingQuestionnaire.riskProfile?.level || 'סיכון נמוך',
            existingQuestionnaire.expiresAt
          )

        ) : (
          /* Wizard */
          <>
            {error && (
              <div className="mb-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                <AlertCircle size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}
            <OnboardingStepper />
            <QuestionnaireWizard onComplete={handleQuestionnaireComplete} />

            {/* Why are we asking? — explainer link */}
            <a
              href={`${import.meta.env.VITE_MARKETING_URL || 'https://iftach-advise.com'}/onboarding?return=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 mx-auto flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all group max-w-fit"
            >
              <HelpCircle size={16} className="text-blue-400 group-hover:text-blue-600 flex-shrink-0 transition-colors" />
              <span>למה אנחנו שואלים את השאלות האלה?</span>
              <span className="font-semibold underline underline-offset-2 decoration-blue-400">קראו כאן</span>
            </a>
          </>
        )}
      </div>

      {/* Saving overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 mx-4 flex flex-col items-center gap-3">
            <div className="spinner w-10 h-10" />
            <p className="font-medium text-gray-900">שומר את השאלון...</p>
            <p className="text-xs text-gray-500 text-center">מחשב את פרופיל הסיכון שלך</p>
          </div>
        </div>
      )}
    </div>
  )
}
